import type { ReactNode } from 'react';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { TimestampFormat } from '@/contexts/TimestampFormatContext';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';

import { IntegrationCard } from './IntegrationCard';
import { renderSyncIcon, type LastSyncStatus } from './syncIcon';

export type SyncCardStatus = 'connected' | 'not_connected' | 'error';

export interface SyncRendererConfig {
  testIdPrefix: string;
  displayName: string;
}

export function renderStatusCopy(
  cfg: SyncRendererConfig,
  cardStatus: SyncCardStatus
): ReactNode {
  if (cardStatus === 'connected') {
    return (
      <span
        data-testid={`${cfg.testIdPrefix}-status-copy`}
        className="text-xs text-muted-foreground"
      >
        Your {cfg.displayName} workspace is connected
      </span>
    );
  }
  if (cardStatus === 'not_connected') {
    return (
      <span
        data-testid={`${cfg.testIdPrefix}-status-copy`}
        className="text-xs text-muted-foreground"
      >
        Connect your {cfg.displayName} workspace to get started
      </span>
    );
  }
  return null;
}

export function renderRunningIndicator(
  cfg: SyncRendererConfig,
  isRunning: boolean | undefined
): ReactNode {
  if (!isRunning) return null;
  return (
    <div
      data-testid={`${cfg.testIdPrefix}-syncing-indicator`}
      className="flex items-center gap-2 text-sm text-muted-foreground"
    >
      <Loader2 className="h-4 w-4 animate-spin" />
      <span>Syncing users from {cfg.displayName}...</span>
    </div>
  );
}

export function renderSyncNote(
  cfg: SyncRendererConfig,
  syncNote: string | undefined
): ReactNode {
  const message =
    syncNote ??
    `Users in your ${cfg.displayName} workspace are periodically synced to Kirin with an Imported status in the Members tab.`;
  return (
    <Alert
      data-testid={`${cfg.testIdPrefix}-sync-note`}
      variant="default"
      className="bg-muted/50"
    >
      <AlertDescription className="text-sm">
        <strong>Note:</strong> {message}
      </AlertDescription>
    </Alert>
  );
}

export function renderErrorAlert(
  cfg: SyncRendererConfig,
  error: string | undefined
): ReactNode {
  if (!error) return null;
  return (
    <Alert
      data-testid={`${cfg.testIdPrefix}-error-alert`}
      variant="destructive"
    >
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>{error}</AlertDescription>
    </Alert>
  );
}

export function renderErrorBadgeSlot(
  cfg: SyncRendererConfig,
  cardStatus: SyncCardStatus
): ReactNode {
  if (cardStatus !== 'error') return null;
  return (
    <IntegrationCard.StatusBadge>
      <Badge
        data-testid={`${cfg.testIdPrefix}-error-pill`}
        variant="destructive"
        className="gap-1.5"
      >
        <AlertCircle className="h-3.5 w-3.5" />
        Error
      </Badge>
    </IntegrationCard.StatusBadge>
  );
}

export interface RenderConnectButtonArgs {
  onConnect: (() => void) | undefined;
  tooltip: string;
  isPending?: boolean;
  readOnly?: boolean;
}

export function renderConnectButton(
  cfg: SyncRendererConfig,
  args: RenderConnectButtonArgs
): ReactNode {
  const { onConnect, tooltip, isPending = false, readOnly } = args;
  if (!onConnect) return null;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            data-testid={`${cfg.testIdPrefix}-connect-button`}
            onClick={onConnect}
            disabled={isPending || (readOnly ?? false)}
            size="sm"
            className="flex-shrink-0 gap-2"
          >
            {isPending ? 'Connecting...' : 'Connect'}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export interface RenderSyncButtonArgs {
  onSync: (() => void) | undefined;
  isSyncing: boolean;
  readOnly?: boolean;
}

export function renderSyncButton(
  cfg: SyncRendererConfig,
  args: RenderSyncButtonArgs
): ReactNode {
  const { onSync, isSyncing, readOnly } = args;
  if (!onSync) return null;
  return (
    <Button
      data-testid={`${cfg.testIdPrefix}-sync-button`}
      variant="outline"
      size="sm"
      onClick={onSync}
      disabled={isSyncing || (readOnly ?? false)}
      className="flex-shrink-0 gap-2"
    >
      {isSyncing ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Syncing...
        </>
      ) : (
        <>
          <RefreshCw className="h-4 w-4" />
          Sync Now
        </>
      )}
    </Button>
  );
}

export interface RenderDisconnectButtonArgs {
  onDisconnect: (() => void) | undefined;
  isSyncing: boolean;
  isPending?: boolean;
  readOnly?: boolean;
}

export function renderDisconnectButton(
  cfg: SyncRendererConfig,
  args: RenderDisconnectButtonArgs
): ReactNode {
  const { onDisconnect, isSyncing, isPending = false, readOnly } = args;
  if (!onDisconnect) return null;
  return (
    <Button
      data-testid={`${cfg.testIdPrefix}-disconnect-button`}
      variant="ghost"
      size="sm"
      onClick={onDisconnect}
      disabled={isSyncing || isPending || (readOnly ?? false)}
    >
      {isPending ? 'Disconnecting...' : 'Disconnect'}
    </Button>
  );
}

export function renderTryAgainButton(
  cfg: SyncRendererConfig,
  onConnect: (() => void) | undefined,
  readOnly?: boolean
): ReactNode {
  if (!onConnect) return null;
  return (
    <Button
      data-testid={`${cfg.testIdPrefix}-try-again-button`}
      variant="outline"
      size="sm"
      onClick={onConnect}
      disabled={readOnly ?? false}
    >
      Try Again
    </Button>
  );
}

export interface RenderMetaArgs {
  orgLabel: string;
  orgName: string | undefined | null;
  syncLabel: string;
  importedCount: number | undefined;
  linkedCount: number | undefined;
  lastSyncAt: string | undefined | null;
  lastSyncStatus: LastSyncStatus;
  connectedAt: string | undefined | null;
  // True iff a syncStatus prop was supplied by the caller. Matches the
  // legacy hasAnyMeta gate, which renders the meta region whenever sync
  // status is present even if every field inside it is empty.
  hasSyncStatus: boolean;
  // The per-user Timestamp Format setting, threaded in by the calling card
  // (this is a render helper, not a component, so it can't read the context
  // hook itself). Drives whether "Last sync" shows relative or absolute.
  timestampFormat: TimestampFormat;
}

export function renderMeta(
  cfg: SyncRendererConfig,
  args: RenderMetaArgs
): ReactNode {
  const {
    orgLabel,
    orgName,
    syncLabel,
    importedCount,
    linkedCount,
    lastSyncAt,
    lastSyncStatus,
    connectedAt,
    hasSyncStatus,
    timestampFormat,
  } = args;

  const hasAnyMeta =
    hasSyncStatus ||
    typeof importedCount === 'number' ||
    Boolean(orgName) ||
    Boolean(connectedAt);
  if (!hasAnyMeta) return null;

  const chips: ReactNode[] = [];

  if (orgName && orgName.length > 0) {
    chips.push(
      <span key="org" data-testid={`${cfg.testIdPrefix}-meta-org`}>
        {orgLabel}: <span className="font-mono text-foreground">{orgName}</span>
      </span>
    );
  }

  if (typeof importedCount === 'number') {
    chips.push(
      <span key="imported" data-testid={`${cfg.testIdPrefix}-meta-imported`}>
        Imported: {importedCount} users
      </span>
    );
  }

  if (typeof linkedCount === 'number' && linkedCount > 0) {
    chips.push(
      <span key="linked" data-testid={`${cfg.testIdPrefix}-meta-linked`}>
        Linked: {linkedCount} users
      </span>
    );
  }

  if (lastSyncAt) {
    // Inline metadata chip (not a table cell): use the shared
    // RelativeTimeWithTooltip in `inline` mode so it renders a <span> trigger
    // that nests validly inside this inline <span> chip AND gets the same Radix
    // tooltip + Timestamp Format handling as every other timestamp surface
    // (the prior native-`title` form was hover-only / not SR-announced, and
    // hand-rolled the relative/absolute logic). `format` is passed explicitly
    // because this is a render helper, not a component, so it can't read the
    // context hook itself.
    chips.push(
      <span key="sync" data-testid={`${cfg.testIdPrefix}-meta-sync`}>
        {syncLabel}:{' '}
        <RelativeTimeWithTooltip
          date={lastSyncAt}
          format={timestampFormat}
          showIcon={false}
          inline
        />
        {renderSyncIcon(lastSyncStatus)}
      </span>
    );
  }

  if (connectedAt) {
    // "Connected on <date>" is a fixed calendar-date label, not an "ago"
    // timestamp — it has no relative form, so it stays a plain locale date
    // and is intentionally NOT governed by the Timestamp Format setting.
    chips.push(
      <span
        key="connected-on"
        data-testid={`${cfg.testIdPrefix}-meta-connected-on`}
      >
        Connected on {new Date(connectedAt).toLocaleDateString()}
      </span>
    );
  }

  if (chips.length === 0) return null;
  return <IntegrationCard.Meta>{chips}</IntegrationCard.Meta>;
}
